
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Eye, RotateCcw, Trash2, Send, Building2, User, Mail, CheckCircle, Clock } from 'lucide-react';
import type { Application } from '@/types/database.types';

interface ApplicationCardProps {
  application: Application;
  onView: (applicationId: string) => void;
  onResetRequest: (applicationId: string) => void;
  onDeleteRequest: (applicationId: string) => void;
  onSubmit: (applicationId: string) => void;
  isActionInProgress: boolean;
}

export const ApplicationCard = ({
  application,
  onView,
  onResetRequest,
  onDeleteRequest,
  onSubmit,
  isActionInProgress
}: ApplicationCardProps) => {
  // Get the display progress based on application status
  const getDisplayProgress = (): number => {
    return application.status === 'submitted' ? 100 : (application.progress || 0);
  };

  return (
    <Card key={application.id} className="glass-card w-full overflow-hidden flex flex-col group border-primary/10 hover:shadow-elevation-2 transition-all duration-300 hover:translate-y-[-2px]">
      <CardHeader className="p-3 bg-gradient-to-r from-gray-50 to-white border-b">
        <CardTitle className="text-base truncate group-hover:text-primary transition-colors flex items-center gap-2">
          <Building2 className="h-4 w-4 text-primary/70" />
          {application.company_legal_name || 'Unnamed Company'}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-2 flex-grow">
        <p className="text-xs flex justify-between items-center">
          <span className="text-gray-700 font-medium flex items-center gap-1.5">
            <CheckCircle className={`h-3.5 w-3.5 ${application.status === 'submitted' ? 'text-primary' : 'text-amber-500'}`} />
            Status:
          </span> 
          <span className={`${application.status === 'submitted' ? 'text-primary font-medium' : 'text-amber-500 font-medium'} px-1.5 py-0.5 rounded bg-gray-50`}>
            {application.status}
          </span>
        </p>
        <p className="text-xs flex justify-between items-center">
          <span className="text-gray-700 font-medium flex items-center gap-1.5">
            <User className="h-3.5 w-3.5 text-gray-500" />
            Applicant:
          </span> 
          <span>{application.first_name} {application.last_name}</span>
        </p>
        <p className="text-xs flex justify-between items-center">
          <span className="text-gray-700 font-medium flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-gray-500" />
            Email:
          </span> 
          <span className="truncate max-w-[180px]">{application.email}</span>
        </p>
        <p className="text-xs flex justify-between items-center">
          <span className="text-gray-700 font-medium flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-gray-500" />
            Progress:
          </span> 
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full"
                style={{ width: `${getDisplayProgress()}%` }}
              ></div>
            </div>
            <span>{getDisplayProgress()}%</span>
          </div>
        </p>
        {application.is_non_profit && (
          <p className="text-xs flex justify-between mt-1">
            <span className="text-gray-700 font-medium">Type:</span> 
            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">501(c)(3) Non-Profit</span>
          </p>
        )}
      </CardContent>
      <CardFooter className="p-3 border-t flex justify-between bg-gray-50/80">
        <div className="flex gap-1">
          <Button 
            variant="glass" 
            size="icon" 
            className="h-7 w-7" 
            onClick={() => onView(application.id)}
            title="View Application"
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          
          <Button 
            variant="glass" 
            size="icon" 
            className="h-7 w-7" 
            onClick={() => onResetRequest(application.id)}
            title="Reset Progress"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
        
        <div className="flex gap-1">
          {application.status !== 'submitted' && (
            <Button 
              variant="glass-primary" 
              size="icon"
              className="h-7 w-7" 
              onClick={() => onSubmit(application.id)}
              disabled={isActionInProgress}
              title="Submit Application"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
          
          <Button 
            variant="destructive" 
            size="icon" 
            className="h-7 w-7" 
            onClick={() => onDeleteRequest(application.id)}
            title="Delete Application"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
